import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  TrendingUp, 
  DollarSign, 
  FileText, 
  Percent,
  ArrowUpRight,
  ChevronRight
} from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/context/AuthContext';
import { KYCBanner } from '@/components/KYCBanner';
import { SubscriptionBanner, SubscriptionStatus } from '@/components/SubscriptionBanner';
import { formatCurrency, formatDate, getStatusColor } from '@/lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LenderDashboard() {
  const { user, token } = useAuth();
  const [stats, setStats] = useState(null);
  const [rfbs, setRfbs] = useState([]);
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  // Handle Stripe redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const payment = params.get('payment');
    if (sessionId && payment === 'success' && token) {
      fetch(`${API}/subscriptions/checkout/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(r => r.json())
        .then(data => {
          if (data.payment_status === 'paid') setPaymentSuccess(true);
        })
        .catch(() => {});
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [token]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, rfbsRes, bidsRes] = await Promise.all([
          axios.get(`${API}/dashboard/lender`),
          axios.get(`${API}/loans/requests?status=pending`),
          axios.get(`${API}/bids`)
        ]);
        setStats(statsRes.data);
        setRfbs(rfbsRes.data.slice(0, 5));
        setBids(bidsRes.data.slice(0, 5));
      } catch (err) {
        console.error('Failed to fetch dashboard data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const statCards = [
    {
      icon: DollarSign,
      label: 'Total Lent',
      value: formatCurrency(stats?.total_lent || 0),
      color: 'text-green-500'
    },
    {
      icon: FileText,
      label: 'Active Loans',
      value: stats?.active_loans || 0,
      color: 'text-blue-500'
    },
    {
      icon: Percent,
      label: 'Avg. Rate',
      value: `${stats?.average_rate || 0}%`,
      color: 'text-purple-500'
    },
    {
      icon: TrendingUp,
      label: 'Total Earnings',
      value: formatCurrency(stats?.total_earnings || 0),
      color: 'text-yellow-500'
    }
  ];

  if (loading) {
    return (
      <DashboardLayout title="Dashboard">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Dashboard">
      <KYCBanner />
      <SubscriptionBanner />

      {paymentSuccess && (
        <div className="flex items-center gap-3 p-4 rounded-xl bg-green-500/10 border border-green-500/20 mb-5" data-testid="payment-success">
          <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
            <TrendingUp className="w-4 h-4 text-green-400" />
          </div>
          <div>
            <p className="font-medium text-green-400 text-sm">Subscription Activated!</p>
            <p className="text-xs text-gray-400">You can now place bids on loan requests. Happy lending!</p>
          </div>
        </div>
      )}

      {/* Welcome */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold mb-2">Welcome back, {user?.name}!</h2>
            <p className="text-muted-foreground">
              Here's your lending portfolio overview.
            </p>
          </div>
          <SubscriptionStatus />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {statCards.map((stat, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
          >
            <Card className="glass border-white/10">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <stat.icon className={`w-8 h-8 ${stat.color}`} />
                  <ArrowUpRight className="w-5 h-5 text-muted-foreground" />
                </div>
                <div className="text-3xl font-bold mb-1">{stat.value}</div>
                <div className="text-sm text-muted-foreground">{stat.label}</div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Available RFBs */}
        <Card className="glass border-white/10">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>
              <span className="flex items-center gap-2">
                Available RFBs
                <Badge className="bg-primary/20 text-primary">
                  {stats?.available_rfbs || 0}
                </Badge>
              </span>
            </CardTitle>
            <Link to="/lender/rfbs">
              <Button variant="ghost" size="sm">
                View All
                <ChevronRight className="ml-1 w-4 h-4" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {rfbs.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No loan requests available</p>
              </div>
            ) : (
              <div className="space-y-3">
                {rfbs.map((rfb) => (
                  <Link 
                    key={rfb.id} 
                    to={`/lender/rfbs/${rfb.id}`}
                    className="block"
                  >
                    <div className="flex items-center justify-between p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors">
                      <div>
                        <div className="font-medium">{formatCurrency(rfb.amount)}</div>
                        <div className="text-sm text-muted-foreground">
                          {rfb.purpose} • {rfb.term_months}mo
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-medium">
                          {rfb.bids_count} bids
                        </div>
                        {rfb.best_rate && (
                          <div className="text-sm text-green-500">
                            Best: {rfb.best_rate}%
                          </div>
                        )}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* My Recent Bids */}
        <Card className="glass border-white/10">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>My Recent Bids</CardTitle>
            <Link to="/lender/bids">
              <Button variant="ghost" size="sm">
                View All
                <ChevronRight className="ml-1 w-4 h-4" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {bids.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No bids placed yet</p>
                <Link to="/lender/rfbs">
                  <Button className="mt-4 gradient-primary">
                    Browse Loan Requests
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {bids.map((bid) => (
                  <div 
                    key={bid.id} 
                    className="flex items-center justify-between p-4 rounded-xl bg-white/5"
                  >
                    <div>
                      <div className="font-medium">{bid.interest_rate}% APR</div>
                      <div className="text-sm text-muted-foreground">
                        {formatDate(bid.created_at)}
                      </div>
                    </div>
                    <Badge className={getStatusColor(bid.status)}>
                      {bid.status}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
